/**
 * Keyboard Shortcuts Provider
 *
 * React context for managing keyboard shortcuts throughout the application.
 * Provides access to the keyboard manager, actions, and preferences.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  toggleCommandPalette,
  toggleSidebar,
  toggleSearch,
  toggleSettings,
} from '@/store/slices/uiSlice';
import { selectFile } from '@/store/slices/filesSlice';
import { useCreateFileMutation } from '@/store/api/unifiedStorageApi';
import { toast } from 'sonner';

import {
  keyboardManager,
  KeyboardShortcutManager,
} from '@/core/services/KeyboardShortcutManager';
import { getDefaultShortcuts } from '@/core/services/DefaultShortcuts';
import type {
  ShortcutAction,
  ShortcutBinding,
  ShortcutConflict,
  ShortcutPreferences,
  ShortcutContext,
} from '@/core/types/keyboard';

interface KeyboardShortcutsContextValue {
  /** Keyboard manager instance */
  manager: KeyboardShortcutManager;
  /** All registered actions */
  actions: ShortcutAction[];
  /** All key bindings */
  bindings: ShortcutBinding[];
  /** Current conflicts */
  conflicts: ShortcutConflict[];
  /** User preferences */
  preferences: ShortcutPreferences;
  /** Set current context */
  setContext: (context: ShortcutContext) => void;
  /** Update preferences */
  updatePreferences: (updates: Partial<ShortcutPreferences>) => void;
  /** Create custom binding */
  createBinding: (actionId: string, keys: string) => Promise<boolean>;
  /** Remove binding */
  removeBinding: (actionId: string) => void;
  /** Enable/disable action */
  setActionEnabled: (actionId: string, enabled: boolean) => void;
  /** Get conflicts for key combination */
  checkConflicts: (keys: string) => ShortcutConflict[];
  /** Reset to defaults */
  resetToDefaults: () => void;
  /** Force refresh data */
  refresh: () => void;
}

const KeyboardShortcutsContext =
  createContext<KeyboardShortcutsContextValue | null>(null);

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
}

export function KeyboardShortcutsProvider({
  children,
}: KeyboardShortcutsProviderProps) {
  const dispatch = useAppDispatch();
  const { currentProject } = useAppSelector((state) => state.project);
  const [createFile] = useCreateFileMutation();

  // State for reactive updates
  const [actions, setActions] = useState<ShortcutAction[]>([]);
  const [bindings, setBindings] = useState<ShortcutBinding[]>([]);
  const [conflicts, setConflicts] = useState<ShortcutConflict[]>([]);
  const [preferences, setPreferences] = useState<ShortcutPreferences>(() =>
    keyboardManager.getPreferences()
  );

  // Refresh data from manager
  const refresh = useCallback(() => {
    setActions(keyboardManager.getActions());
    setBindings(keyboardManager.getBindings());
    setConflicts(keyboardManager.getAllConflicts());
    setPreferences(keyboardManager.getPreferences());
  }, []);

  // Set context wrapper
  const setContext = useCallback((context: ShortcutContext) => {
    keyboardManager.setContext(context);
  }, []);

  // Update preferences wrapper
  const updatePreferences = useCallback(
    (updates: Partial<ShortcutPreferences>) => {
      keyboardManager.updatePreferences(updates);
      setPreferences(keyboardManager.getPreferences());
    },
    []
  );

  // Create binding wrapper
  const createBinding = useCallback(
    async (actionId: string, keysStr: string): Promise<boolean> => {
      const keys = keyboardManager.stringToKeyCombo(keysStr);
      if (!keys) {
        toast.error('Invalid key combination format');
        return false;
      }

      const conflicts = keyboardManager.detectConflicts(keys, actionId);
      const errorConflicts = conflicts.filter((c) => c.severity === 'error');

      if (errorConflicts.length > 0) {
        toast.error(
          `Shortcut conflicts with: ${errorConflicts[0].actions.join(', ')}`
        );
        return false;
      }

      try {
        keyboardManager.createBinding(actionId, keys, true);
        refresh();
        toast.success('Shortcut updated successfully');
        return true;
      } catch (error) {
        toast.error('Failed to update shortcut');
        return false;
      }
    },
    [refresh]
  );

  // Remove binding wrapper
  const removeBinding = useCallback(
    (actionId: string) => {
      keyboardManager.removeBinding(actionId);
      refresh();
      toast.success('Shortcut removed');
    },
    [refresh]
  );

  // Enable/disable action wrapper
  const setActionEnabled = useCallback(
    (actionId: string, enabled: boolean) => {
      keyboardManager.setActionEnabled(actionId, enabled);
      refresh();
    },
    [refresh]
  );

  // Check conflicts wrapper
  const checkConflicts = useCallback((keysStr: string): ShortcutConflict[] => {
    const keys = keyboardManager.stringToKeyCombo(keysStr);
    if (!keys) return [];
    return keyboardManager.detectConflicts(keys);
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    keyboardManager.resetPreferences();
    // Re-register default shortcuts
    initializeShortcuts();
    refresh();
    toast.success('Shortcuts reset to defaults');
  }, []);

  // Initialize shortcuts with handlers
  const initializeShortcuts = useCallback(() => {
    const handlers = {
      // File operations
      newDocument: async () => {
        if (!currentProject) {
          toast.error('Please select a project first');
          return;
        }

        try {
          const timestamp = new Date().toISOString().split('T')[0];
          const name = `untitled-${timestamp}.md`;
          const content = `---
title: Untitled Document
type: document
tags: []
created: ${new Date().toISOString()}
---

# Untitled Document

Start writing here...
`;

          const result = await createFile({
            name,
            content,
            type: 'document',
            tags: [],
            projectId: currentProject.id,
            metadata: {
              title: 'Untitled Document',
              created: new Date().toISOString(),
            },
          }).unwrap();

          dispatch(selectFile(result.id));
          toast.success('New document created');
        } catch (error) {
          toast.error('Failed to create document');
        }
      },

      newPhrase: async () => {
        if (!currentProject) {
          toast.error('Please select a project first');
          return;
        }

        try {
          const timestamp = new Date().toISOString().split('T')[0];
          const name = `phrase-${timestamp}.md`;
          const content = `---
title: New Phrase
type: phrase
tags: []
created: ${new Date().toISOString()}
---

Your reusable phrase content here...
`;

          const result = await createFile({
            name,
            content,
            type: 'phrase',
            tags: [],
            projectId: currentProject.id,
            metadata: {
              title: 'New Phrase',
              created: new Date().toISOString(),
            },
          }).unwrap();

          dispatch(selectFile(result.id));
          toast.success('New phrase created');
        } catch (error) {
          toast.error('Failed to create phrase');
        }
      },

      openFile: () => {
        dispatch(toggleCommandPalette());
      },

      save: () => {
        // This will be handled by the editor component
        const event = new CustomEvent('phrasewright:save');
        window.dispatchEvent(event);
      },

      saveAs: () => {
        // This will be handled by the editor component
        const event = new CustomEvent('phrasewright:save-as');
        window.dispatchEvent(event);
      },

      closeFile: () => {
        // This will be handled by the editor component
        const event = new CustomEvent('phrasewright:close-file');
        window.dispatchEvent(event);
      },

      // Edit operations
      undo: () => {
        document.execCommand('undo');
      },

      redo: () => {
        document.execCommand('redo');
      },

      cut: () => {
        document.execCommand('cut');
      },

      copy: () => {
        document.execCommand('copy');
      },

      paste: () => {
        document.execCommand('paste');
      },

      selectAll: () => {
        document.execCommand('selectAll');
      },

      find: () => {
        const event = new CustomEvent('phrasewright:find');
        window.dispatchEvent(event);
      },

      replace: () => {
        const event = new CustomEvent('phrasewright:replace');
        window.dispatchEvent(event);
      },

      // View operations
      toggleSidebar: () => {
        dispatch(toggleSidebar());
      },

      togglePreview: () => {
        const event = new CustomEvent('phrasewright:toggle-preview');
        window.dispatchEvent(event);
      },

      toggleMinimap: () => {
        const event = new CustomEvent('phrasewright:toggle-minimap');
        window.dispatchEvent(event);
      },

      zoomIn: () => {
        const event = new CustomEvent('phrasewright:zoom-in');
        window.dispatchEvent(event);
      },

      zoomOut: () => {
        const event = new CustomEvent('phrasewright:zoom-out');
        window.dispatchEvent(event);
      },

      resetZoom: () => {
        const event = new CustomEvent('phrasewright:reset-zoom');
        window.dispatchEvent(event);
      },

      toggleFullscreen: () => {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          document.documentElement.requestFullscreen();
        }
      },

      // Navigation
      goToLine: () => {
        const event = new CustomEvent('phrasewright:goto-line');
        window.dispatchEvent(event);
      },

      nextTab: () => {
        const event = new CustomEvent('phrasewright:next-tab');
        window.dispatchEvent(event);
      },

      prevTab: () => {
        const event = new CustomEvent('phrasewright:prev-tab');
        window.dispatchEvent(event);
      },

      closeTab: () => {
        const event = new CustomEvent('phrasewright:close-tab');
        window.dispatchEvent(event);
      },

      // Workflow
      commandPalette: () => {
        dispatch(toggleCommandPalette());
      },

      quickSearch: () => {
        dispatch(toggleCommandPalette());
      },

      globalSearch: () => {
        dispatch(toggleSearch());
      },

      // Reference system
      insertReference: () => {
        const event = new CustomEvent('phrasewright:insert-reference');
        window.dispatchEvent(event);
      },

      quickReference: () => {
        const event = new CustomEvent('phrasewright:quick-reference');
        window.dispatchEvent(event);
      },

      goToReference: () => {
        const event = new CustomEvent('phrasewright:goto-reference');
        window.dispatchEvent(event);
      },

      // Format
      bold: () => {
        const event = new CustomEvent('phrasewright:format-bold');
        window.dispatchEvent(event);
      },

      italic: () => {
        const event = new CustomEvent('phrasewright:format-italic');
        window.dispatchEvent(event);
      },

      insertCode: () => {
        const event = new CustomEvent('phrasewright:insert-code');
        window.dispatchEvent(event);
      },

      insertCodeBlock: () => {
        const event = new CustomEvent('phrasewright:insert-code-block');
        window.dispatchEvent(event);
      },

      insertHeader: (level: number) => {
        const event = new CustomEvent('phrasewright:insert-header', {
          detail: { level },
        });
        window.dispatchEvent(event);
      },

      insertList: () => {
        const event = new CustomEvent('phrasewright:insert-list');
        window.dispatchEvent(event);
      },

      insertOrderedList: () => {
        const event = new CustomEvent('phrasewright:insert-ordered-list');
        window.dispatchEvent(event);
      },

      insertLink: () => {
        const event = new CustomEvent('phrasewright:insert-link');
        window.dispatchEvent(event);
      },

      // System
      settings: () => {
        dispatch(toggleSettings());
      },

      help: () => {
        const event = new CustomEvent('phrasewright:show-help');
        window.dispatchEvent(event);
      },

      shortcuts: () => {
        const event = new CustomEvent('phrasewright:show-shortcuts');
        window.dispatchEvent(event);
      },

      devTools: () => {
        // Let browser handle F12
      },
    };

    const shortcuts = getDefaultShortcuts(handlers);
    keyboardManager.registerActions(shortcuts);

    // Create default bindings
    shortcuts.forEach((_action) => {
      // Find the default key combination from the action definition
      // This would typically be stored with the action
      // For now, we'll let the DefaultShortcuts handle the binding creation
    });
  }, [currentProject, createFile, dispatch]);

  // Initialize on mount
  useEffect(() => {
    initializeShortcuts();
    refresh();
  }, [initializeShortcuts, refresh]);

  // Update when dependencies change
  useEffect(() => {
    // Re-register handlers when project changes
    initializeShortcuts();
  }, [currentProject, initializeShortcuts]);

  const contextValue: KeyboardShortcutsContextValue = {
    manager: keyboardManager,
    actions,
    bindings,
    conflicts,
    preferences,
    setContext,
    updatePreferences,
    createBinding,
    removeBinding,
    setActionEnabled,
    checkConflicts,
    resetToDefaults,
    refresh,
  };

  return (
    <KeyboardShortcutsContext.Provider value={contextValue}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
}

/**
 * Hook to use keyboard shortcuts context
 */
export function useKeyboardShortcuts() {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error(
      'useKeyboardShortcuts must be used within a KeyboardShortcutsProvider'
    );
  }
  return context;
}

/**
 * Hook to use keyboard shortcuts in a specific context
 */
export function useKeyboardContext(context: ShortcutContext) {
  const { setContext } = useKeyboardShortcuts();

  useEffect(() => {
    setContext(context);
    return () => setContext('global');
  }, [setContext, context]);
}

/**
 * Hook to register custom keyboard shortcut
 */
export function useKeyboardShortcut(
  keys: string,
  handler: () => void,
  deps: React.DependencyList = []
) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Simple key matching for custom shortcuts
      // This could be enhanced to use the full manager system
      if (event.code === keys || event.key === keys) {
        event.preventDefault();
        handler();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [keys, handler, ...deps]);
}
